import React from "react"
import { Renderer } from './Components/Renderer'
import { Tooltip } from './Components/Tooltip'
import useWindowSize from "hooks/useWindowSize"
import { formatMoneyHeatmap } from "helpers"
import styles from './Heatmap.module.scss'

export type InteractionData = {
  xLabel: string;
  yLabel: string;
  xPos: number;
  yPos: number;
  value: number;
};

type HeatmapDatum = { x: string; y: string; value: number | null };

interface IHeatmap {
  width: number;
  height: number;
  data: HeatmapDatum[];
}

/**
 * Sort all SKUs by most-missed (most practitioners with gaps) first.
 * Sort practitioners by gap count descending (most gaps at top).
 * Returns the full sorted data + stats.
 */
function sortData(data: HeatmapDatum[]) {
  const practitioners = [...new Set(data.map(d => d.y))];
  const skus = [...new Set(data.map(d => d.x))];

  // Build a lookup: (practitioner, sku) -> value
  const lookup = new Map<string, number | null>();
  for (const d of data) {
    lookup.set(`${d.y}||${d.x}`, d.value);
  }

  // For each SKU, count how many practitioners have a gap (0 or null)
  const skuGapCount = new Map<string, number>();
  for (const sku of skus) {
    let gaps = 0;
    for (const p of practitioners) {
      const v = lookup.get(`${p}||${sku}`);
      if (!v) gaps++;
    }
    skuGapCount.set(sku, gaps);
  }

  // Sort all SKUs: most-missed first, then by total revenue for ties
  const skuRevenue = new Map<string, number>();
  for (const sku of skus) {
    let total = 0;
    for (const p of practitioners) {
      total += lookup.get(`${p}||${sku}`) ?? 0;
    }
    skuRevenue.set(sku, total);
  }
  const sortedSkus = [...skus].sort((a, b) => {
    const gapDiff = (skuGapCount.get(b) ?? 0) - (skuGapCount.get(a) ?? 0);
    if (gapDiff !== 0) return gapDiff;
    return (skuRevenue.get(b) ?? 0) - (skuRevenue.get(a) ?? 0);
  });

  // Sort practitioners: most gaps first
  const practitionerGapCount = new Map<string, number>();
  for (const p of practitioners) {
    let gaps = 0;
    for (const sku of skus) {
      const v = lookup.get(`${p}||${sku}`);
      if (!v) gaps++;
    }
    practitionerGapCount.set(p, gaps);
  }
  const sortedPractitioners = [...practitioners].sort(
    (a, b) => (practitionerGapCount.get(b) ?? 0) - (practitionerGapCount.get(a) ?? 0)
  );

  // Build practitioner stats
  const practitionerStats = new Map<string, { gaps: number; totalRevenue: number }>();
  for (const p of sortedPractitioners) {
    let gaps = 0;
    let revenue = 0;
    for (const sku of skus) {
      const v = lookup.get(`${p}||${sku}`);
      if (!v) gaps++;
      else revenue += v;
    }
    practitionerStats.set(p, { gaps, totalRevenue: revenue });
  }

  return {
    sortedSkus,
    sortedPractitioners,
    lookup,
    totalSkuCount: skus.length,
    practitionerStats,
  };
}

function buildVisibleData(
  sortedSkus: string[],
  sortedPractitioners: string[],
  lookup: Map<string, number | null>,
  visibleSkuCount: number,
) {
  const visibleSkus = sortedSkus.slice(0, visibleSkuCount);
  const result: HeatmapDatum[] = [];
  let gaps = 0;
  for (const p of sortedPractitioners) {
    for (const sku of visibleSkus) {
      const v = lookup.get(`${p}||${sku}`) ?? null;
      result.push({ x: sku, y: p, value: v });
      if (!v) gaps++;
    }
  }
  const totalCells = sortedPractitioners.length * visibleSkus.length;
  return { data: result, gaps, totalCells, visibleSkuCount: visibleSkus.length };
}

const Heatmap: React.FC<IHeatmap> = ({ width, height, data }) => {
  const [hoveredCell, setHoveredCell] = React.useState<InteractionData | null>(null);
  const [selectedCell, setSelectedCell] = React.useState<InteractionData | null>(null);
  const [skuLimit, setSkuLimit] = React.useState(5);
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth <= 480;

  const {
    sortedSkus,
    sortedPractitioners,
    lookup,
    totalSkuCount,
    practitionerStats,
  } = React.useMemo(() => sortData(data), [data]);

  // Build dropdown options: 5, 10, 15... up to totalSkuCount, plus "All"
  const dropdownOptions = React.useMemo(() => {
    const options: { value: number; label: string }[] = [];
    for (let i = 5; i < totalSkuCount; i += 5) {
      options.push({ value: i, label: `${i}` });
    }
    options.push({ value: totalSkuCount, label: `All (${totalSkuCount})` });
    return options;
  }, [totalSkuCount]);

  const effectiveLimit = Math.min(skuLimit, totalSkuCount);

  const { data: activeData, gaps, totalCells, visibleSkuCount } = React.useMemo(
    () => buildVisibleData(sortedSkus, sortedPractitioners, lookup, effectiveLimit),
    [sortedSkus, sortedPractitioners, lookup, effectiveLimit]
  );

  const completionPct = totalCells > 0
    ? Math.round(((totalCells - gaps) / totalCells) * 100)
    : 0;

  // Scale width so cells don't get too compressed
  const minColWidth = 70;
  const labelSpace = isMobile ? 10 : 230;
  const neededWidth = labelSpace + visibleSkuCount * minColWidth;
  const rendererWidth = Math.max(width, neededWidth);

  const footerHeight = 50;
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [measuredHeaderHeight, setMeasuredHeaderHeight] = React.useState(isMobile ? 160 : 70);

  React.useEffect(() => {
    if (headerRef.current) {
      setMeasuredHeaderHeight(headerRef.current.offsetHeight);
    }
  }, [selectedCell, isMobile, completionPct]);

  const chartHeight = height - measuredHeaderHeight - footerHeight;

  // On mobile, use onClick (tap) to select; on desktop use hover
  const handleCellInteraction = isMobile
    ? (cell: InteractionData | null) => { if (cell) setSelectedCell(cell); }
    : setHoveredCell;

  return (
    <div className={styles.wrapper} style={{ width, height, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div ref={headerRef} style={{ padding: '12px 16px 8px', flexShrink: 0 }}>
        {/* Context label */}
        <div style={{ fontSize: isMobile ? 15 : 18, color: '#333', fontWeight: 700, marginBottom: 8 }}>
          Showing your top 10 practitioners by Hero SKU revenue
        </div>

        {/* Progress bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            flex: 1,
            height: 8,
            backgroundColor: '#f0f0f0',
            borderRadius: 4,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${completionPct}%`,
              height: '100%',
              backgroundColor: completionPct === 100 ? '#479D7F' : '#5BA8C8',
              borderRadius: 4,
              transition: 'width 0.3s ease',
            }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#333', whiteSpace: 'nowrap' }}>
            {completionPct}% covered &middot; {gaps} gap{gaps !== 1 ? 's' : ''} remaining
          </span>
        </div>

        {/* Mobile: selected cell info panel */}
        {isMobile && (
          <div style={{
            marginTop: 10,
            padding: '8px 12px',
            backgroundColor: selectedCell ? '#f8f8f8' : '#fafafa',
            borderRadius: 8,
            border: '1px solid #eee',
            minHeight: 54,
          }}>
            {selectedCell ? (
              <>
                <div style={{ fontSize: 13, marginBottom: 2 }}>
                  <span style={{ fontWeight: 600 }}>Practitioner: </span>
                  {selectedCell.yLabel}
                </div>
                <div style={{ fontSize: 13, marginBottom: 2 }}>
                  <span style={{ fontWeight: 600 }}>Product: </span>
                  {selectedCell.xLabel}
                </div>
                <div style={{ fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>Sales This Year: </span>
                  <span style={{ color: selectedCell.value === 0 ? '#B9261A' : '#1B5E20', fontWeight: 600 }}>
                    {formatMoneyHeatmap(selectedCell.value)}
                  </span>
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: '#999' }}>
                Tap a cell to see details
              </div>
            )}
          </div>
        )}
      </div>

      {/* Scrollable chart area */}
      <div style={{
        flex: 1,
        overflowX: 'auto',
        overflowY: 'hidden',
        position: 'relative',
      }}>
        {activeData.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 14 }}>
            No data available.
          </div>
        ) : (
          <>
            <Renderer
              width={rendererWidth}
              height={chartHeight}
              data={activeData}
              setHoveredCell={handleCellInteraction}
              practitionerStats={practitionerStats}
            />
            {!isMobile && (
              <Tooltip interactionData={hoveredCell} width={rendererWidth} height={chartHeight} />
            )}
          </>
        )}
      </div>

      {/* Footer: SKU count dropdown */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        padding: '8px 16px',
        flexShrink: 0,
        borderTop: '1px solid #eee',
      }}>
        <span style={{ fontSize: 15, color: '#555', fontWeight: 500 }}>Showing</span>
        <select
          value={effectiveLimit}
          onChange={(e) => setSkuLimit(Number(e.target.value))}
          style={{
            padding: '5px 10px',
            fontSize: 15,
            fontWeight: 500,
            borderRadius: 6,
            border: '1px solid #ccc',
            backgroundColor: 'white',
            cursor: 'pointer',
          }}
        >
          {dropdownOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <span style={{ fontSize: 15, color: '#555', fontWeight: 500 }}>
          / {totalSkuCount} Hero SKUs
        </span>
      </div>
    </div>
  );
}

export default Heatmap